import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { mockResources } from '../data/mock';
import { FileText, BookOpen, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const ResourceDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const resource = mockResources.find((r) => String(r.id) === id);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'blog':
        return FileText;
      case 'case-study':
        return BookOpen;
      default:
        return FileText;
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      blog: { label: 'Blog Post', color: 'bg-blue-100 text-blue-700' },
      'case-study': { label: 'Case Study', color: 'bg-green-100 text-green-700' },
    };
    return badges[type] || badges.blog;
  };

  if (!resource) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-32 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Resource not found</h1>
          <Button onClick={() => navigate('/resources')} variant="outline">
            Back to Resources
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const Icon = getTypeIcon(resource.type);
  const badge = getTypeBadge(resource.type);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <article className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:bg-slate-100 mb-8 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <header className="mb-10">
            <span
              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${badge.color} mb-4`}
            >
              <Icon className="w-3.5 h-3.5" />
              {badge.label}
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              {resource.title}
            </h1>
            <div className="flex items-center gap-2 text-gray-500">
              <span>{resource.date}</span>
              <span>•</span>
              <span>{resource.readTime}</span>
            </div>
          </header>

          <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden mb-10 bg-gray-200">
            <img
              src={resource.image}
              alt={resource.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="prose prose-slate prose-lg max-w-none">
            {resource.content?.split('\n\n').map((paragraph, i) => (
              <p key={i} className="text-gray-600 mb-6 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <Button
              variant="outline"
              onClick={() => navigate('/resources')}
              className="border-slate-600 text-slate-600 hover:bg-slate-50"
            >
              View All Resources
            </Button>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default ResourceDetailPage;
